<template>
  <PageHeader
    :title="editing ? editing.title : 'Agent Pages'"
    :breadcrumbs="editing ? [{ label: 'Admin Console' }, { label: 'Agent Pages', to: { name: 'AdminAgentPages' } }] : [{ label: 'Admin Console' }]"
  />

  <div class="p-4">
    <div v-if="error" class="text-sm text-red-600">{{ error }}</div>

    <template v-else-if="pageId">
      <LoadingIndicator v-if="!editing" message="Loading page..." />
      <PageEditor
        v-else-if="isSpec"
        :key="pageId"
        :page-id="pageId"
        :spec="editing.spec"
        :widgets="editing.widgets || {}"
        :category="editing.category || ''"
        :categories="editing.categories || []"
        @saved="backToList"
        @cancel="backToList"
      />
      <div v-else class="text-sm text-gray-600">This page is an HTML document written by the agent. Edit it through the agent.</div>
    </template>

    <div v-else class="bg-white rounded-lg border border-gray-200 overflow-x-auto">
      <LoadingIndicator v-if="loading" message="Loading pages..." />
      <table v-else class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Page</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shows in</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kind</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Author</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Saved</th>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-if="!pages.length">
            <td colspan="6" class="px-6 py-8 text-center text-sm text-gray-500">No agent pages yet.</td>
          </tr>
          <tr v-for="p in pages" :key="p.id">
            <td class="px-6 py-4 text-sm font-medium text-gray-900">{{ p.title }}</td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{{ p.category_label || 'Insights V2 only' }}</td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
              {{ p.kind === 'dashboard' ? `Dashboard · ${p.placements} widget${p.placements === 1 ? '' : 's'}` : 'Document' }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{{ p.created_by }}</td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{{ when(p.updated_at) }}</td>
            <td class="px-6 py-4 whitespace-nowrap text-right text-sm">
              <router-link :to="`/agent/page/${p.id}`" class="text-gray-600 hover:text-gray-900 mr-4">Open</router-link>
              <router-link
                v-if="p.kind === 'dashboard'"
                :to="{ name: 'AdminAgentPageEdit', params: { id: p.id } }"
                class="text-blue-600 hover:text-blue-800"
              >Edit</router-link>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import PageEditor from '@/components/Beta/widgets/PageEditor.vue';

export default {
  name: 'AgentPages_Admin',
  components: { PageHeader, LoadingIndicator, PageEditor },
  data() {
    return { pages: [], loading: true, editing: null, error: '' };
  },
  computed: {
    pageId() {
      return Number(this.$route.params.id) || null;
    },
    isSpec() {
      return !!(this.editing && this.editing.spec && (this.editing.spec.placements || []).length);
    },
  },
  watch: {
    pageId: { immediate: true, handler: 'load' },
  },
  methods: {
    async load() {
      this.error = '';
      if (this.pageId) {
        this.editing = null;
        try {
          const { data } = await this.$axios.get(`/api/impact_analytics/agent-pages/${this.pageId}/`);
          this.editing = data;
        } catch (e) {
          this.error = e?.response?.status === 404 ? 'No page with that name.' : 'That page could not be loaded.';
        }
        return;
      }
      this.loading = true;
      try {
        const { data } = await this.$axios.get('/api/impact_analytics/agent-pages/');
        this.pages = data;
      } catch {
        this.error = 'Pages could not be loaded.';
      } finally {
        this.loading = false;
      }
    },
    backToList() {
      this.$router.push({ name: 'AdminAgentPages' });
    },
    when(iso) {
      if (!iso) return '';
      return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    },
  },
};
</script>
